/* =========================================================
   AF — All-Reviews Slider data loader
   File: assets/af-all-reviews-slider.js

   Fetches approved reviews from across the store and builds the slides,
   then hands off to the shared slider behaviour (af-reviews-slider.js).
     GET /apps/store/reviews/all  → { ok:true, reviews:[ … ] }
   - Runs independently of the form (defer); the form never waits on it.
   - Hidden until MORE than `data-min-reviews` approved reviews exist across the
     store (auto — re-checked every load). Empty/too-few/any failure → the whole
     band stays hidden.
   - product_title / product_image may be empty → fall back to the handle,
     never drop a review.
   - Slide images carry srcset + sizes + explicit width/height (Task 1b).

   Self-contained IIFE with a per-instance init guard (house AF pattern).
   ========================================================= */
(() => {
  "use strict";

  const LIMIT = 50;
  const SRCSET_WIDTHS = [48, 96, 144];
  const IMG_SIZES = "40px";
  const DEFAULT_W = 96;

  const isShopifyCdn = (u) => {
    try { return /(^|\.)cdn\.shopify\.com$/i.test(new URL(u, location.href).hostname); }
    catch (e) { return false; }
  };
  const cdnW = (url, w) =>
    isShopifyCdn(url) ? url + (url.indexOf("?") >= 0 ? "&" : "?") + "width=" + w : url;
  const buildSrcset = (url) =>
    isShopifyCdn(url) ? SRCSET_WIDTHS.map((w) => cdnW(url, w) + " " + w + "w").join(", ") : "";

  // The /reviews/all API returns HTML-entity-encoded text (e.g. "&amp;",
  // "&#039;"). Decode via a textarea (RCDATA — tags are never parsed as
  // elements, so this is XSS-safe) so textContent shows the real characters.
  const decodeEntities = (s) => {
    const str = (s || "") + "";
    if (str.indexOf("&") === -1) return str;
    const ta = document.createElement("textarea");
    ta.innerHTML = str;
    return ta.value;
  };

  const titleize = (handle) =>
    ((handle || "") + "").replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();

  const CHECK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  const buildSlide = (r) => {
    const rating = Math.max(0, Math.min(5, parseInt(r.rating, 10) || 0));
    const handle = (r.product_handle || "").trim();
    const productName = decodeEntities(r.product_title).trim() || (handle ? titleize(handle) : "");

    const li = document.createElement("li");
    li.className = "af-rvs__slide";

    const card = document.createElement("article");
    card.className = "af-rvs__card af-arvs-card";

    // Stars + a visible "N / 5" so the score reads at a glance, not just by count
    const ratingRow = document.createElement("div");
    ratingRow.className = "af-arvs-card__rating";
    const stars = document.createElement("div");
    stars.className = "af-rvs__stars";
    stars.setAttribute("role", "img");
    stars.setAttribute("aria-label", "Rating " + rating + " out of 5");
    for (let i = 1; i <= 5; i++) {
      const s = document.createElement("span");
      s.className = "af-rvs__star" + (i <= rating ? " is-on" : "");
      s.setAttribute("aria-hidden", "true");
      stars.appendChild(s);
    }
    ratingRow.appendChild(stars);
    const rnum = document.createElement("span");
    rnum.className = "af-arvs-card__rating-num";
    rnum.textContent = rating + " / 5";
    rnum.setAttribute("aria-hidden", "true");
    ratingRow.appendChild(rnum);
    card.appendChild(ratingRow);

    // Title
    if ((r.title || "").trim()) {
      const h = document.createElement("h3");
      h.className = "af-arvs-card__title";
      h.textContent = decodeEntities(r.title);
      card.appendChild(h);
    }

    // Body
    const body = document.createElement("div");
    body.className = "af-rvs__text af-arvs-card__body";
    body.textContent = decodeEntities(r.body);
    card.appendChild(body);

    // Divider
    const div = document.createElement("div");
    div.className = "af-rvs__divider";
    div.setAttribute("aria-hidden", "true");
    card.appendChild(div);

    // Foot: product (image + name, linked) + reviewer
    const foot = document.createElement("div");
    foot.className = "af-rvs__meta af-arvs-card__foot";

    if (productName) {
      const prodTag = handle ? document.createElement("a") : document.createElement("div");
      prodTag.className = "af-arvs-card__product";
      if (handle) prodTag.href = "/products/" + handle;

      const img = (r.product_image || "").trim();
      if (img) {
        const im = document.createElement("img");
        im.className = "af-arvs-card__img";
        im.alt = "";
        im.loading = "lazy";
        im.decoding = "async";
        im.width = 40;
        im.height = 40;
        const srcset = buildSrcset(img);
        if (srcset) { im.srcset = srcset; im.sizes = IMG_SIZES; im.src = cdnW(img, DEFAULT_W); }
        else { im.src = img; }
        prodTag.appendChild(im);
      }
      const pn = document.createElement("span");
      pn.className = "af-arvs-card__product-name";
      pn.textContent = productName;
      prodTag.appendChild(pn);
      foot.appendChild(prodTag);
    }

    const who = document.createElement("div");
    who.className = "af-arvs-card__who";
    const name = document.createElement("div");
    name.className = "af-rvs__name";
    name.textContent = decodeEntities(r.name).trim() || "Anonymous";
    who.appendChild(name);
    if (r.verified) {
      const vb = document.createElement("div");
      vb.className = "af-arvs-card__verified";
      vb.innerHTML = CHECK_SVG;
      const vt = document.createElement("span");
      vt.textContent = "Verified buyer";
      vb.appendChild(vt);
      who.appendChild(vb);
    }
    foot.appendChild(who);

    card.appendChild(foot);
    li.appendChild(card);
    return li;
  };

  // The shared carousel cancels the click after any >6px drag (for momentum
  // scrolling), which also eats genuine taps on the product links. Navigate on a
  // real tap/click here — gated on movement + duration — so the links always work,
  // desktop and mobile, without changing the shared drag behaviour.
  const wireTapNav = (sliderEl) => {
    let x = 0, y = 0, t = 0, moved = false, armed = false;
    const link = (el) => (el && el.closest ? el.closest(".af-arvs-card__product[href]") : null);
    sliderEl.addEventListener("pointerdown", (e) => {
      armed = !!link(e.target);
      moved = false;
      if (armed) { x = e.clientX; y = e.clientY; t = Date.now(); }
    });
    sliderEl.addEventListener("pointermove", (e) => {
      if (armed && (Math.abs(e.clientX - x) > 8 || Math.abs(e.clientY - y) > 8)) moved = true;
    });
    sliderEl.addEventListener("pointercancel", () => { armed = false; });
    sliderEl.addEventListener("pointerup", (e) => {
      if (!armed) return;
      armed = false;
      const a = link(e.target);
      if (a && !moved && Date.now() - t < 600) window.location.href = a.getAttribute("href");
    });
  };

  const loadSliderBehaviour = (root) => {
    const src = root.dataset.sliderSrc;
    if (!src) return;
    if (window.__afRvsBehaviourLoaded) return; // load the shared script once per page
    window.__afRvsBehaviourLoaded = true;
    const s = document.createElement("script");
    s.src = src;
    s.defer = true;
    document.head.appendChild(s);
  };

  const initOne = (root) => {
    if (!root || root.dataset.afArvsInit === "1") return;
    root.dataset.afArvsInit = "1";

    const url = root.dataset.reviewsUrl || "/apps/store/reviews/all";
    const slider = root.querySelector("[data-arvs-slider]");
    if (!slider) return;

    // Auto-gate: stay hidden until MORE than this many approved reviews exist.
    // Baked default 20 (matches the Liquid default) so it holds even if the
    // section setting is still nil on the live theme.
    const parsedMin = parseInt(root.dataset.minReviews, 10);
    const minReviews = Number.isFinite(parsedMin) ? parsedMin : 20;
    // Show only the latest N (endpoint returns newest-first). Baked default 30.
    const parsedMax = parseInt(root.dataset.maxSlides, 10);
    const maxSlides = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : 30;

    fetch(url + "?limit=" + LIMIT, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        const all = data && data.ok && Array.isArray(data.reviews) ? data.reviews : [];
        // Showcase = written testimonials only. Star-only (bodyless) reviews still
        // count in the aggregates elsewhere, but an empty slide reads as broken here.
        const reviews = all.filter((r) => (r.body || "").trim());
        // Collapse the reserved band unless the count clears the threshold.
        if (reviews.length <= minReviews) { root.hidden = true; return; }

        const frag = document.createDocumentFragment();
        reviews.slice(0, maxSlides).forEach((r) => frag.appendChild(buildSlide(r)));
        slider.innerHTML = "";
        slider.appendChild(frag);
        wireTapNav(slider); // product links must survive the shared drag-to-click cancel

        root.hidden = false; // reveal before booting the behaviour (needs layout)
        requestAnimationFrame(() => root.classList.add("is-in")); // fade the band in
        loadSliderBehaviour(root);
      })
      .catch(() => {
        /* fetch failed or bad JSON → collapse the reserved band, never show an error */
        root.hidden = true;
      });
  };

  const initAll = () => {
    document.querySelectorAll("[data-af-arvs]").forEach(initOne);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", initAll);
})();
